import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { push } from 'react-router-redux';
import { closeModal } from '../../actions/ModalActions';
import Modal from '../Modal';
import discountApi from './discountApi';
import { couponTerms, utcLocalInput, utcPayload } from '../../utils/discountFormat';

/** "0.5000" -> "50" */
function toPercentField(rate) {
    return rate === null || rate === undefined || rate === '' ? '' : String(Number((Number(rate) * 100).toFixed(2)));
}

/**
 * Add a promotion code to a coupon, or edit one. Laid out like Stripe's
 * "Add promotion code": the code, then the customer-eligibility and
 * redemption restrictions. In Candy a promotion is Stripe's promotion
 * code plus the campaign detail Fergus needs — countries, commission
 * impact, scheme — so those sit under Restrictions and Internal. The
 * coupon is fixed once the promotion has left draft. Countries map to
 * every non-deprecated billing_region row of that country (V2.1).
 */
@connect(
    () => ({}),
    dispatch => bindActionCreators({ closeModal, push }, dispatch)
)
export default class PromotionFormModal extends Component {
    constructor(props) {
        super(props);
        var p = props.promotion;
        var coupon = props.coupon || (p ? p.coupon : null);
        this.state = {
            isSaving: false,
            error: null,
            coupons: coupon ? [coupon] : (props.coupons || null),
            form: {
                coupon_id: p ? (p.coupon_id || '') : (props.couponId || ''),
                code: p ? (p.code || '') : '',
                name: p ? p.name : '',
                first_time_only: p ? p.customer_eligibility === 'new' : false,
                existing_only: p ? p.customer_eligibility === 'existing' : false,
                countries: p ? (p.countries || []) : [],
                // The API stores and compares redemption instants in UTC and
                // returns them as ISO-8601 in UTC; the inputs show and take UTC
                // wall time (labelled), so no local-time conversion happens.
                redemption_starts_at: p && p.redemption_starts_at ? utcLocalInput(p.redemption_starts_at) : '',
                limit_expiry: !!(p && p.redemption_ends_at),
                redemption_ends_at: p && p.redemption_ends_at ? utcLocalInput(p.redemption_ends_at) : '',
                scheme_type: p ? p.scheme_type : (props.reference.enums.scheme_types[0] || 'marketing'),
                commission_impact: p && p.commission_impact_ratio !== null && p.commission_impact_ratio !== undefined ? toPercentField(p.commission_impact_ratio) : '',
                auto_apply: p ? !!p.auto_apply : false,
                priority: p ? p.priority : 100,
                showInternal: false
            }
        };
    }

    componentDidMount() {
        if (this.state.coupons === null) {
            discountApi.listCoupons()
                .then(coupons => this.setState({ coupons: coupons.filter(c => c.state !== 'archived') }))
                .catch(error => this.setState({ coupons: [], error: error.message }));
        }
    }

    countries() {
        return this.props.reference.countries || [];
    }

    set(field, value) {
        this.setState({ form: { ...this.state.form, [field]: value } });
    }

    toggleCountry(country) {
        var countries = this.state.form.countries;
        this.set('countries', countries.includes(country) ? countries.filter(c => c != country) : countries.concat([country]));
    }

    regionIds() {
        return this.props.reference.regions
            .filter(r => this.state.form.countries.includes(r.country))
            .map(r => r.billing_region_id);
    }

    handleSubmit() {
        var f = this.state.form;
        var body = {
            name: f.name || f.code,
            coupon_id: f.coupon_id || null,
            code: f.code || null,
            customer_eligibility: f.first_time_only ? 'new' : (f.existing_only ? 'existing' : 'any'),
            commission_impact_ratio: f.commission_impact === '' ? null : (Number(f.commission_impact) / 100).toFixed(4),
            auto_apply: f.auto_apply,
            priority: Number(f.priority),
            redemption_starts_at: utcPayload(f.redemption_starts_at),
            redemption_ends_at: f.limit_expiry ? utcPayload(f.redemption_ends_at) : null,
            region_ids: this.regionIds()
        };
        if (this.props.promotion && this.props.promotion.status !== 'draft') {
            // The coupon is locked in once the promotion has left draft
            delete body.coupon_id;
        }
        var request = this.props.promotion
            ? discountApi.updatePromotion(this.props.promotion.promotion_id, { ...body, version: this.props.promotion.version })
            : discountApi.createPromotion({ ...body, scheme_type: f.scheme_type });

        this.setState({ isSaving: true, error: null });
        request.then(promotion => {
            this.props.closeModal();
            if (this.props.onDone) {
                this.props.onDone(promotion);
            }
            if (!this.props.promotion && !this.props.stayOnPage) {
                this.props.push(`/discounts/promotions/${promotion.promotion_id}`);
            }
        }).catch(error => this.setState({ isSaving: false, error: error.message }));
    }

    render() {
        var { form, isSaving, error, coupons } = this.state;
        var enums = this.props.reference.enums;
        var editing = !!this.props.promotion;
        var couponLocked = !!this.props.couponId || (editing && this.props.promotion.status !== 'draft');
        var chosen = (coupons || []).find(c => c.coupon_id === form.coupon_id);
        var canSubmit = (form.name || form.code) && form.countries.length > 0 && !isSaving;
        var actionButton = (
            <button className={`btn btn-success ${canSubmit ? '' : 'disabled'}`} onClick={canSubmit ? this.handleSubmit.bind(this) : undefined}>
                {isSaving ? 'Saving...' : (editing ? 'Save' : 'Add promotion code')}
            </button>
        );
        var section = title => <h5 style={{ marginTop: 18, marginBottom: 8, textTransform: 'uppercase', fontSize: 11, letterSpacing: 1 }} className="text-muted">{title}</h5>;

        return (
            <Modal actionButton={actionButton} className="modal-primary" title={editing ? 'Edit promotion code' : 'Add promotion code'}>
                {error && <div className="alert alert-danger">{error}</div>}

                <div className="form-group">
                    <label>Coupon</label>
                    {couponLocked && chosen && <div className="form-control-static">{chosen.invoice_name} <span className="text-muted">— {couponTerms(chosen)}</span></div>}
                    {couponLocked && !chosen && <div className="form-control-static text-muted">{form.coupon_id ? 'loading...' : 'none yet'}</div>}
                    {!couponLocked && coupons === null && <p className="text-muted">Loading coupons...</p>}
                    {!couponLocked && coupons !== null &&
                        <select className="form-control" value={form.coupon_id} onChange={e => this.set('coupon_id', e.target.value)}>
                            <option value="">choose later...</option>
                            {coupons.map(c => <option key={c.coupon_id} value={c.coupon_id}>{c.invoice_name} — {couponTerms(c)}{c.state === 'draft' ? ' (draft)' : ''}</option>)}
                        </select>
                    }
                    {!couponLocked && <small className="text-muted">The terms this promotion gives. Fixed once the promotion leaves draft.</small>}
                </div>

                <div className="row">
                    <div className="col-xs-6">
                        <div className="form-group">
                            <label>Code</label>
                            <input className="form-control" value={form.code} maxLength={32} placeholder="JUNIORPROMO" onChange={e => this.set('code', e.target.value)} />
                            <small className="text-muted">Letters, digits, - and _. Optional: leave blank for a promotion applied by admins or at signup.</small>
                        </div>
                    </div>
                    <div className="col-xs-6">
                        <div className="form-group">
                            <label>Name <small className="text-muted">internal</small></label>
                            <input className="form-control" value={form.name} maxLength={200} placeholder={form.code || 'e.g. Junior apprentices, spring 2026'} onChange={e => this.set('name', e.target.value)} />
                        </div>
                    </div>
                </div>

                {section('Restrictions')}
                <div className="checkbox">
                    <label>
                        <input type="checkbox" checked={form.first_time_only} onChange={e => this.setState({ form: { ...form, first_time_only: e.target.checked, existing_only: false } })} /> Eligible for first-time customers only <small className="text-muted">no paid plan before</small>
                    </label>
                </div>
                <div className="checkbox">
                    <label>
                        <input type="checkbox" checked={form.existing_only} onChange={e => this.setState({ form: { ...form, existing_only: e.target.checked, first_time_only: false } })} /> Existing customers only
                    </label>
                </div>
                <div className="checkbox">
                    <label>
                        <input type="checkbox" checked={form.limit_expiry} onChange={e => this.set('limit_expiry', e.target.checked)} /> Add an expiry date
                    </label>
                </div>
                {form.limit_expiry &&
                    <div className="row" style={{ marginLeft: 6 }}>
                        <div className="col-xs-6">
                            <div className="form-group">
                                <label>Redeemable from <small className="text-muted">optional, UTC</small></label>
                                <input type="datetime-local" className="form-control" value={form.redemption_starts_at} onChange={e => this.set('redemption_starts_at', e.target.value)} />
                            </div>
                        </div>
                        <div className="col-xs-6">
                            <div className="form-group">
                                <label>Expires <small className="text-muted">UTC</small></label>
                                <input type="datetime-local" className="form-control" value={form.redemption_ends_at} onChange={e => this.set('redemption_ends_at', e.target.value)} />
                            </div>
                        </div>
                    </div>
                }
                <div className="form-group">
                    <label>Countries <small className="text-muted">at least one; matched on the client's billing region</small></label>
                    <div>
                        {this.countries().map(c =>
                            <label key={c.code} className="checkbox-inline">
                                <input type="checkbox" checked={form.countries.includes(c.code)} onChange={() => this.toggleCountry(c.code)} /> {c.name}
                            </label>
                        )}
                    </div>
                </div>
                <div className="checkbox">
                    <label>
                        <input type="checkbox" checked={form.auto_apply} onChange={e => this.set('auto_apply', e.target.checked)} /> Apply automatically at paid signup <small className="text-muted">for matching customers, no code needed</small>
                    </label>
                </div>

                {section('Internal')}
                <div className="row">
                    <div className="col-xs-4">
                        <div className="form-group">
                            <label>Scheme</label>
                            <select className="form-control" value={form.scheme_type} disabled={editing} onChange={e => this.set('scheme_type', e.target.value)}>
                                {enums.scheme_types.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="col-xs-4">
                        <div className="form-group">
                            <label>Commission impact</label>
                            <div className="input-group">
                                <input type="number" min={0} max={100} className="form-control" value={form.commission_impact} placeholder={chosen ? `inherit ${toPercentField(chosen.commission_impact_ratio)}` : 'inherit'} onChange={e => this.set('commission_impact', e.target.value)} />
                                <span className="input-group-addon">%</span>
                            </div>
                            <small className="text-muted">blank = the coupon's{chosen ? ` (${toPercentField(chosen.commission_impact_ratio)}%)` : ''}; set to override</small>
                        </div>
                    </div>
                    <div className="col-xs-4">
                        <div className="form-group">
                            <label>Priority <small className="text-muted">tie-break only</small></label>
                            <input type="number" min={0} max={65535} className="form-control" value={form.priority} onChange={e => this.set('priority', e.target.value)} />
                        </div>
                    </div>
                </div>
            </Modal>
        );
    }
}



// WEBPACK FOOTER //
// ./app/components/discounts/PromotionFormModal.js